import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import Image from 'next/image'
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import { CartContext } from '../../../context/cartContext'
import {
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
    SUBTLE_BORDER_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
    CustomizationContext,
} from '../../../context/CustomizationContextProvider'
import { PageContext } from '../../../context/PageContextProvider'
import { QueryParamContext } from '../../../context/QueryParamContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { useEscapeToDismiss } from '../../../hooks/useEscapeToDismiss'
import { useDesktopModal } from '../../../hooks/useDesktopModal'
import {
    PAGE_WIDTH_VAR,
    scaledFontSize,
    SECTION_CONTAINER_NAME,
    ScrollingLayout,
} from '../../../style/scrollingLayout'
import { getTypographyRoleStyle } from '../../../style/typography'
import { hoverTintSurface } from '../../../style/interactiveStates'
import { CartItem, CartItemStatus, StockStatus } from '../../../types'
import { priceFormat } from '../../../utils/priceFormat'
import { FormattedPriceDisplay } from '../../shared/PriceDisplay'
import { ButtonLoadingSpinner, TrashIcon } from '../../icons/Icons'
import { CartFooter } from './CartFooter'
import {
    useItemDiscount,
    useCartDiscounts,
} from '../../../hooks/useCartDiscounts'
import { DiscountErrorBoundary } from '../../shared/DiscountErrorBoundary'
import {
    DiscountPercentageBadgeComponent,
    DiscountAppliedBadgeComponent,
    SaleSavingsBadge,
} from './CartItemDiscountBadges'
import {
    shouldShowCartSubscription,
    useSubscriptionOptions,
} from '../../../utils/subscriptions'
import { CartSubscription } from './CartSubscription'
import { QuantityStepper } from './QuantityStepper'
import { CartUpsells } from './CartUpsells'
import { EmptyCartProductGrid } from './EmptyCartProductGrid'
import { DrawerHeader } from './DrawerHeader'
import { useHeaderScrollVisibility } from '../../../hooks/useHeaderScrollVisibility'
import { AnalyticsContext } from '../../../context/analyticsContext'
import {
    getCartItemAnimationKey,
    getCartItemReactKey,
    isCartItemEditDisabled,
    isManagedGiftCartItem,
    isStubbedCartItem,
    sortCartItemsForDisplay,
} from '../../../utils/cartTools'
import { getDisplayData } from '../../../utils/productDisplay'
import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import { FreeShippingProgressBar } from './FreeShippingProgressBar'
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion'
import { InspectOverlayHost } from '../../shared/InspectOverlayHost'

/**
 * The panel arrives from the edge it is anchored to, which tells the shopper
 * where it came from and where closing will send it back to. Kept short
 * enough not to sit between them and the cart.
 */
const CART_SLIDE_MS = 220

const slideInFromRight = keyframes`
    from {
        transform: translateX(100%);
    }
    to {
        transform: translateX(0);
    }
`

const opacityAnimation = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`

const LinkOverlayContainer = styled(InspectOverlayHost)({
    position: 'fixed',
    inset: 0,
    flexDirection: 'column',
    animation: `${opacityAnimation} 0.1s ease-in`,
    pointerEvents: 'none',
    // A panel beside the page, so the shopper keeps sight of what they
    // were looking at. Full screen on a phone, where there is no beside.
    [ScrollingLayout.DESKTOP_MEDIA]: {
        width: ScrollingLayout.CART_PANEL_WIDTH,
        left: 'auto',
        right: 0,
        animation: `${slideInFromRight} ${CART_SLIDE_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
        '@media (prefers-reduced-motion: reduce)': {
            animation: `${opacityAnimation} 0.1s ease-in`,
        },
        // Cards in the panel are the same cards as on the page, so they read
        // the same container. The page width is rebased with it, or anything
        // inside that bleeds would reach for the window instead of the panel.
        containerType: 'inline-size',
        containerName: SECTION_CONTAINER_NAME,
        [PAGE_WIDTH_VAR]: `${ScrollingLayout.CART_PANEL_WIDTH}px`,
    },
})

/** Dims the page behind the cart panel, and closes it when clicked. */
const CartScrim = styled(InspectOverlayHost)({
    display: 'none',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        display: 'block',
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'auto',
        // Fades over the same beat the panel travels in, so the two read as
        // one movement rather than a flash followed by a slide.
        animation: `${opacityAnimation} ${CART_SLIDE_MS}ms ease-out`,
    },
})

const OpaqueContainer = styled.div({
    pointerEvents: 'auto',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
})

const CartContainer = styled.div({
    flex: 1,
    width: '100%',
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    paddingBottom: '40px',
})

const CartEmptyState = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    width: '100%',
    ...getTypographyRoleStyle('bodyText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(18),
    padding: '64px 0 32px 0',
})

const CartItemsContainer = styled.div({
    padding: '0 16px',
})

const cartItemEntrance = keyframes`
    from {
        opacity: 0;
        transform: scale(0.9) translateY(8px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
`

const CartItemContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '14px 0',
    borderBottom: `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
    '&.newly-added': {
        animation: `${cartItemEntrance} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.25s both`,
    },
})

const EMPTY_STRING_SET = new Set<string>()

const ANIMATION_DURATION_MS = 650

const CartItemContentRow = styled.div({
    display: 'flex',
    width: '100%',
})

const ItemInfoContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    fontSize: scaledFontSize(14),
    width: '100%',
    position: 'relative',
})

// Cart item layout constants
const CART_ITEM_IMAGE_WIDTH = 120
const CART_ITEM_IMAGE_HEIGHT = 120
const CART_ITEM_GIFT_IMAGE_HEIGHT = CART_ITEM_IMAGE_HEIGHT / 2

const ItemInfoTopRow = styled.div({
    ...getTypographyRoleStyle('captionText', {
        fontWeight: 400,
        lineHeight: 1.3,
    }),
    fontSize: scaledFontSize(15),
    marginBottom: 4,
    paddingRight: 115,
})

const ItemInfoBottomRow = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    ...getTypographyRoleStyle('captionText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(14),
})

const PriceContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
})

const PriceRow = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
})

const ListPriceText = styled.div({
    fontSize: scaledFontSize(11),
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    ...getTypographyRoleStyle('compareAtPrice', {
        fontWeight: 400,
    }),
})

const PriceAndQuantityContainer = styled.div({
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
})

const QuantityStepperContainer = styled.div({
    marginBottom: 8,
})

const DiscountBadgesContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginTop: 6,
})

const SubscriptionRowContainer = styled.div({
    marginTop: 8,
    paddingLeft: 132, // 120px image + 12px margin
    width: '100%',
})

const LoadingSpinner = () => {
    return (
        <ButtonLoadingSpinner color={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`} />
    )
}

const DeleteButton = styled.button({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 18,
    border: 'none',
    cursor: 'pointer',
    borderRadius: 4,
    ...hoverTintSurface(),
    ':disabled': {
        cursor: 'not-allowed',
    },
})

// eslint-disable-next-line complexity
const CartListItem: React.FC<{ item: CartItem; isNewlyAdded: boolean }> = ({
    item,
    isNewlyAdded,
}) => {
    const { setActiveLPLink } = useContext(PageContext)
    const { Analytics } = useContext(AnalyticsContext)

    const { landingPage } = useContext(ViddyLPContext)
    const {
        getLinkForProduct,
        fetchAndCacheLinkForProduct,
        resolvedLandingPageLinks,
    } = useContext(LandingPageLinksContext)
    const subscriptionOptions = useSubscriptionOptions()
    const { displayCartSubscriptionOptions } = useContext(CustomizationContext)
    const { removeCartItem, updateCartItem } = useContext(CartContext)
    const { SHOP_CART } = useContext(QueryParamContext)

    const isStubbed = useMemo(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        return isStubbedCartItem(item)
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    }, [item])

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    const productId = item.variant.link?.shopifyId

    const landingPageLink: ApiShopperLandingPageLink | null = useMemo(() => {
        if (!isStubbed) return item.variant.link
        return productId ? (getLinkForProduct(productId) ?? null) : null
    }, [isStubbed, item.variant.link, productId, getLinkForProduct])

    useEffect(() => {
        if (isStubbed && productId) {
            fetchAndCacheLinkForProduct(productId)
        }
    }, [isStubbed, productId, fetchAndCacheLinkForProduct])

    const itemImage = useMemo(() => {
        return (
            item.variant.imageUrl ||
            item.variant.link.imageUrl ||
            item.variant.featuredImage ||
            ''
        )
    }, [item.variant])

    const itemDisplayTitle = useMemo(() => {
        if (!landingPageLink) return item.variant.link?.title ?? ''
        return getDisplayData(
            landingPageLink,
            item.variant.shopifyProductId ?? landingPageLink.shopifyId,
        ).title
    }, [
        landingPageLink,
        item.variant.shopifyProductId,
        item.variant.link?.title,
    ])

    const itemVariantTitle = useMemo(() => {
        // VinylMePlease - hide variant title since we are hiding the variant selector in @VariantSelector.tsx
        // If more than three shops are added, we should make this configurable via admin
        if (landingPage.client.shopifyShop === 'vmp-app.myshopify.com') {
            return null
        }

        if (
            item.variant.title !== item.variant.link?.title &&
            item.variant.title !== 'Default Title' &&
            (item.variant.link?.variantInfo?.length ?? 0) > 1
        ) {
            return item.variant.title
        }

        const variantInfo = item.variant.link?.variantInfo?.find(
            (entry) => entry.shopifyId === item.variant.shopifyId,
        )

        if (variantInfo && (item.variant.link?.variantInfo?.length ?? 0) > 1) {
            return variantInfo.title === 'Default Title'
                ? null
                : variantInfo.title
        }

        return null
    }, [item.variant, landingPage.client.shopifyShop])

    const openProductPage = useCallback(() => {
        if (landingPageLink) {
            Analytics.TRACK_LANDING_PAGE_MENU_ITEM_CLICK(landingPageLink)
            setActiveLPLink(landingPageLink)
        }
    }, [landingPageLink, setActiveLPLink, Analytics])

    const itemNotReady = useMemo(() => isCartItemEditDisabled(item), [item])

    const isManagedGift = isManagedGiftCartItem(item)

    // Only use the available flag if we're on SHOP_CART, otherwise assume the item is available
    const isAvailable = !SHOP_CART || item.variant.available

    const {
        hasDiscount,
        hasSaleSavings,
        applicableDiscountCodes,
        comparePrice,
    } = useItemDiscount(item, resolvedLandingPageLinks, landingPage.discount)

    const cartItemPrice = useMemo(
        () => (
            <PriceAndQuantityContainer>
                <PriceContainer>
                    <PriceRow>
                        <DiscountErrorBoundary>
                            <DiscountPercentageBadgeComponent
                                comparePrice={comparePrice ?? 0}
                                finalPrice={item.variant.finalPrice}
                            />
                        </DiscountErrorBoundary>
                        <FormattedPriceDisplay
                            priceInCents={item.variant.finalPrice}
                            currencySymbol={landingPage.client?.currencySymbol}
                            dollarsSize={16}
                            centsSize={12}
                            topOffset="-0.2em"
                        />
                    </PriceRow>
                    {comparePrice && (
                        <ListPriceText>
                            List Price:{' '}
                            <span style={{ textDecoration: 'line-through' }}>
                                {priceFormat(
                                    comparePrice,
                                    landingPage.client?.currencySymbol,
                                )}
                            </span>
                        </ListPriceText>
                    )}
                </PriceContainer>
            </PriceAndQuantityContainer>
        ),
        [
            comparePrice,
            item.variant.finalPrice,
            landingPage.client?.currencySymbol,
        ],
    )

    const itemImageHeight = isManagedGift
        ? CART_ITEM_GIFT_IMAGE_HEIGHT
        : CART_ITEM_IMAGE_HEIGHT

    return (
        <CartItemContainer className={isNewlyAdded ? 'newly-added' : undefined}>
            <CartItemContentRow>
                <div
                    style={{
                        width: CART_ITEM_IMAGE_WIDTH,
                        height: itemImageHeight,
                        marginRight: 12,
                        flexShrink: 0,
                    }}
                >
                    <Image
                        style={{
                            objectPosition: 'center center',
                            objectFit: 'contain',
                            width: '100%',
                            height: '100%',
                            cursor: !!landingPageLink ? 'pointer' : 'default',
                        }}
                        width={CART_ITEM_IMAGE_WIDTH}
                        height={itemImageHeight}
                        sizes={`${CART_ITEM_IMAGE_WIDTH}px`}
                        src={itemImage}
                        alt={item.variant.title || ''}
                        onClick={openProductPage}
                    />
                </div>
                <ItemInfoContainer>
                    <ItemInfoTopRow>
                        <div
                            style={{
                                ...getTypographyRoleStyle('productTitle', {
                                    fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                                    fontWeight: 400,
                                }),
                                fontSize: scaledFontSize(14),
                                color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                            }}
                        >
                            {itemDisplayTitle}
                        </div>
                        {itemVariantTitle && (
                            <div
                                style={{
                                    ...getTypographyRoleStyle('captionText', {
                                        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                                        fontWeight: 400,
                                    }),
                                    fontSize: scaledFontSize(13),
                                    marginTop: 2,
                                    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
                                }}
                            >
                                {itemVariantTitle}
                            </div>
                        )}
                        {isManagedGift && (
                            <DiscountBadgesContainer>
                                <SaleSavingsBadge label="Free gift" />
                            </DiscountBadgesContainer>
                        )}
                        {hasDiscount && !isManagedGift && (
                            <DiscountBadgesContainer>
                                <DiscountErrorBoundary>
                                    {hasSaleSavings && <SaleSavingsBadge />}
                                    <DiscountAppliedBadgeComponent
                                        discountCodes={applicableDiscountCodes}
                                    />
                                </DiscountErrorBoundary>
                            </DiscountBadgesContainer>
                        )}
                    </ItemInfoTopRow>
                    {isAvailable &&
                        !isManagedGift &&
                        item.status !== CartItemStatus.ADDING && (
                            <QuantityStepperContainer>
                                <QuantityStepper
                                    quantity={item.quantity}
                                    setQuantity={(quantity) => {
                                        if (quantity !== item.quantity) {
                                            updateCartItem(
                                                item.variant,
                                                quantity,
                                                undefined,
                                                false,
                                                undefined,
                                                item.sellingPlanId ?? null,
                                                item.shopifyLineId,
                                            )
                                        }
                                    }}
                                    disabled={itemNotReady}
                                    loading={itemNotReady}
                                    onDelete={() =>
                                        removeCartItem(
                                            item.variant,
                                            item.sellingPlanId ?? null,
                                            item.shopifyLineId,
                                        )
                                    }
                                    isCart={true}
                                />
                            </QuantityStepperContainer>
                        )}
                    {item.status === CartItemStatus.ADDING && (
                        <ItemInfoBottomRow style={{ justifyContent: 'center' }}>
                            <LoadingSpinner />
                        </ItemInfoBottomRow>
                    )}
                    {item.status !== CartItemStatus.ADDING && (
                        <>
                            {isAvailable && cartItemPrice}
                            {!isAvailable && (
                                <ItemInfoBottomRow>
                                    <div style={{ marginRight: 10 }}>
                                        <span
                                            style={{
                                                color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
                                                ...getTypographyRoleStyle(
                                                    'statusText',
                                                    { fontWeight: 300 },
                                                ),
                                                fontSize: scaledFontSize(12),
                                            }}
                                        >
                                            {item.variant?.stockStatus ===
                                            StockStatus.OUT_OF_STOCK
                                                ? 'Out of Stock'
                                                : 'Unavailable'}
                                        </span>
                                    </div>
                                    {!isManagedGift && (
                                        <div style={{ marginRight: 10 }}>
                                            <span>
                                                <DeleteButton
                                                    onClick={() => {
                                                        removeCartItem(
                                                            item.variant,
                                                            item.sellingPlanId ??
                                                                null,
                                                            item.shopifyLineId,
                                                        )
                                                    }}
                                                    disabled={
                                                        item.status ===
                                                        CartItemStatus.REMOVING
                                                    }
                                                >
                                                    {item.status ===
                                                    CartItemStatus.REMOVING ? (
                                                        <LoadingSpinner />
                                                    ) : (
                                                        <TrashIcon
                                                            width={16}
                                                            color={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                                                            disabledColor={`var(${SUBTLE_PRIMARY_COLOR_VAR})`}
                                                        />
                                                    )}
                                                </DeleteButton>
                                            </span>
                                        </div>
                                    )}
                                </ItemInfoBottomRow>
                            )}
                            {!isManagedGift &&
                                item.variant.stockStatus ===
                                    StockStatus.LIMITED_STOCK && (
                                    <ItemInfoBottomRow>
                                        <div style={{ marginRight: 10 }}>
                                            <span
                                                style={{
                                                    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
                                                    ...getTypographyRoleStyle(
                                                        'statusText',
                                                        { fontWeight: 300 },
                                                    ),
                                                    fontSize:
                                                        scaledFontSize(12),
                                                }}
                                            >
                                                Limited stock
                                            </span>
                                        </div>
                                    </ItemInfoBottomRow>
                                )}
                        </>
                    )}
                </ItemInfoContainer>
            </CartItemContentRow>
            {!isManagedGift &&
                shouldShowCartSubscription(
                    item,
                    subscriptionOptions,
                    displayCartSubscriptionOptions,
                ) && (
                    <SubscriptionRowContainer>
                        <CartSubscription
                            cartItem={item}
                            itemNotReady={itemNotReady}
                        />
                    </SubscriptionRowContainer>
                )}
        </CartItemContainer>
    )
}

type ScrollToTopOnAddition = {
    itemCount: number
    settled: boolean
    smooth: boolean
}

/**
 * A new line lands at the top of the list, so a shopper who added from the
 * upsells at the bottom would otherwise see nothing change.
 *
 * Watching the count rather than hanging off the handlers: an addition arrives
 * from the upsells, the variant sheet, a product modal or a recovered cart,
 * through either cart provider, and the count is the one thing they all agree
 * on.
 */
const useScrollToTopOnAddition = (
    scrollContainerRef: React.RefObject<HTMLDivElement | null>,
    { itemCount, settled, smooth }: ScrollToTopOnAddition,
) => {
    const lastCountRef = useRef<number | null>(null)

    /* eslint-disable react-you-might-not-need-an-effect/no-event-handler */
    useEffect(() => {
        if (!settled) return

        const lastCount = lastCountRef.current
        lastCountRef.current = itemCount
        // The first settled count is the cart the shopper arrived with.
        if (lastCount === null || itemCount <= lastCount) return

        scrollContainerRef.current?.scrollTo({
            top: 0,
            behavior: smooth ? 'smooth' : 'auto',
        })
    }, [itemCount, settled, smooth, scrollContainerRef])
    /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */
}

const CartContent: React.FC<{ zIndex?: number | string }> = ({ zIndex }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { resolvedLandingPageLinks } = useContext(LandingPageLinksContext)
    const { cart } = useContext(CartContext)
    const { isCartOpen, setIsCartOpen } = useContext(PageContext)
    const { totalItemCount } = useCartDiscounts(
        cart,
        resolvedLandingPageLinks,
        landingPage.discount,
    )
    const desktopModal = useDesktopModal()
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const isHeaderVisible = useHeaderScrollVisibility(scrollContainerRef)
    const prefersReducedMotion = usePrefersReducedMotion()

    const seenItemsRef = useRef<Set<string> | null>(null)
    const hasSeenLoadingRef = useRef(false)

    useScrollToTopOnAddition(scrollContainerRef, {
        itemCount: totalItemCount,
        settled: !cart.isLoading,
        smooth: isCartOpen && !prefersReducedMotion,
    })

    const displayCartItems = useMemo(
        () => sortCartItemsForDisplay(cart.items),
        [cart.items],
    )

    const newItemIds = useMemo(() => {
        if (cart.isLoading || prefersReducedMotion || !seenItemsRef.current) {
            return EMPTY_STRING_SET
        }

        const newIds = new Set<string>()
        for (const item of displayCartItems) {
            const itemKey = getCartItemAnimationKey(item)
            if (!seenItemsRef.current.has(itemKey)) {
                newIds.add(itemKey)
            }
        }
        return newIds
    }, [displayCartItems, cart.isLoading, prefersReducedMotion])

    useEffect(() => {
        if (cart.isLoading) {
            hasSeenLoadingRef.current = true
            return
        }
        if (!hasSeenLoadingRef.current && seenItemsRef.current === null) return

        const hasNewItems =
            seenItemsRef.current !== null &&
            !prefersReducedMotion &&
            cart.items.some(
                (item) =>
                    !seenItemsRef.current!.has(getCartItemAnimationKey(item)),
            )

        if (hasNewItems) {
            const timer = setTimeout(() => {
                seenItemsRef.current = new Set(
                    cart.items.map((item) => getCartItemAnimationKey(item)),
                )
            }, ANIMATION_DURATION_MS)
            return () => clearTimeout(timer)
        }

        seenItemsRef.current = new Set(
            cart.items.map((item) => getCartItemAnimationKey(item)),
        )
    }, [cart.items, cart.isLoading, prefersReducedMotion])

    const _onNavGridClose = () => {
        setIsCartOpen(false)
    }

    const progressBar = <FreeShippingProgressBar />

    return (
        <OpaqueContainer ref={scrollContainerRef}>
            <DrawerHeader
                onClose={_onNavGridClose}
                centerLabel={
                    totalItemCount > 0
                        ? `Your Cart (${totalItemCount})`
                        : 'Your Cart'
                }
                // The page behind the panel still carries the benefits bar, so
                // repeating it here only pushes the cart itself down.
                bannerText={desktopModal ? undefined : landingPage.bannerText}
                bannerBenefits={landingPage.bannerBenefits}
                isHeaderVisible={isHeaderVisible}
                desktopCloseSide="right"
                // Pinned with the title, rather than scrolling away, so the
                // gap to free shipping stays on screen while the list moves.
                belowHeader={desktopModal ? progressBar : undefined}
            />
            {!desktopModal && progressBar}
            <CartContainer>
                {cart.isLoading && (
                    <CartEmptyState>
                        <div>
                            <LoadingSpinner />
                        </div>
                    </CartEmptyState>
                )}
                {!cart.isLoading && cart.items.length === 0 && (
                    <>
                        <CartEmptyState>
                            <div>Your cart is empty!</div>
                        </CartEmptyState>
                        {/* Gate on isCartOpen so the grid's keen-slider tiles
                            (renderMode: 'performance') always mount inside a
                            display: flex container and read a non-zero
                            offsetWidth at init — otherwise slides render at
                            0 width until something triggers .update(). */}
                        {isCartOpen &&
                            landingPage.dynamicCartUpsellsEnabled && (
                                <EmptyCartProductGrid />
                            )}
                    </>
                )}
                {!cart.isLoading && cart.items.length > 0 && (
                    <CartItemsContainer>
                        {displayCartItems.map((item) => {
                            const animationKey = getCartItemAnimationKey(item)
                            const reactKey = getCartItemReactKey(
                                item,
                                displayCartItems,
                            )
                            return (
                                <CartListItem
                                    key={reactKey}
                                    item={item}
                                    isNewlyAdded={newItemIds.has(animationKey)}
                                />
                            )
                        })}
                    </CartItemsContainer>
                )}
                {<CartUpsells />}
            </CartContainer>
            {!!cart.items.length && <CartFooter zIndex={zIndex} />}
        </OpaqueContainer>
    )
}

export const CartDrawer: React.FC<{ zIndex?: number | string }> = ({
    zIndex = 'unset',
}) => {
    const { isCartOpen, setIsCartOpen } = useContext(PageContext)

    // The panel-plus-scrim variant behaves like a modal, so Escape should
    // close it the same way clicking the scrim does. Not wired up in the
    // full-screen variant, where the drawer's own close button carries the
    // same weight and there is nothing behind it to reveal.
    const close = useCallback(() => setIsCartOpen(false), [setIsCartOpen])
    useEscapeToDismiss(isCartOpen, close)

    return (
        <>
            {isCartOpen && (
                <CartScrim
                    style={{ zIndex }}
                    onClick={() => setIsCartOpen(false)}
                    aria-hidden="true"
                />
            )}
            <LinkOverlayContainer
                role="dialog"
                aria-modal={isCartOpen ? true : undefined}
                aria-label="Cart"
                aria-hidden={!isCartOpen ? true : undefined}
                style={{
                    display: isCartOpen ? 'flex' : 'none',
                    background: isCartOpen
                        ? `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`
                        : 'transparent',
                    zIndex,
                }}
            >
                <CartContent zIndex={zIndex} />
            </LinkOverlayContainer>
        </>
    )
}
